// 멤버십 구독료·결제 화면 순수 로직 (IO 없음) — 2026-09-07
// 계약: docs/ops/toss-payments-impl-spec.md §5-1·§5-3. 입력은 RPC billing_overview() 가 돌려준 객체(overview).
// 「지금 할 일」 우선순위는 §5-3 순서 고정 — 바꾸면 화면과 서버 게이트(can_use_works)가 어긋난다.

import { cardCompanyName } from "./tossCardCompany";
import { tt, ttl } from "../i18n/tt";
import { lazyI18n } from "../i18n/lazyText";

export const BILLING_STATUS_LABEL = lazyI18n(() => Object.freeze({
  none: tt("a.315c3f3d", "결제수단 없음"),
  active: tt("a.3fa4b276", "구독 중"),
  past_due: tt("a.e8f5ebd6", "미납"),
  paused: tt("a.b47e36b8", "휴면"),
  cancel_at_period_end: tt("a.34e62dde", "해지 예약"),
  canceled: tt("a.da2f20d0", "중단됨"),
}));

// 환불 사유 코드 — 서버 subscription_refund.reason_code 체크 제약 · payment-cancel REASON_CODES 와 같은 목록
export const REFUND_REASON_LABEL = lazyI18n(() => Object.freeze({
  safety_window: tt("a.3da8c767", "7일 안전창 (결제 7일 이내 · 이용 없음)"),
  duplicate: tt("a.938a3fc1", "이중 결제"),
  mischarge: tt("a.cb14ae26", "잘못된 청구"),
  tier_error: tt("a.56f48c9a", "등급 산정 오류"),
  access_pass_exception: tt("a.5aee575e", "등급 승급권 예외"),
  other: tt("a.7f598db0", "기타"),
}));

// billing_refund_window.used_reason → 사람이 읽는 말
export const REFUND_USED_REASON_LABEL = lazyI18n(() => Object.freeze({
  work_approved: tt("a.e702d0e7", "결제 뒤 승인된 작품이 있음"),
  inspection_requested: tt("a.31f745fc", "결제 뒤 검수 신청이 있음"),
  downloaded: tt("a.b9107e59", "결제 뒤 소재를 다운로드함"),
  access_pass_started: tt("a.b8911bf4", "등급 승급권은 결제 즉시 개시"),
  access_pass_used: tt("a.ecf45f14", "등급 승급권 사용"),
  window_passed: tt("a.5c96aae1", "결제 후 7일이 지남"),
  no_paid_order: tt("a.93b6f2f3", "결제 건 없음"),
  no_email: tt("a.4ea66b55", "대상 없음"),
}));

export const ORDER_KIND_LABEL = lazyI18n(() => Object.freeze({
  signup_fee: tt("a.fe8ec32e", "가입비"),
  reactivation_fee: tt("a.fc699289", "재활성화비"),
  access_pass: tt("a.713a189c", "등급 승급권"),
  subscription: tt("a.abc3d0d0", "멤버십 구독료"),
}));

export const ORDER_STATUS_LABEL = lazyI18n(() => Object.freeze({
  pending: tt("a.b52aa728", "결제 대기"),
  paid: tt("a.f6c5a4b0", "결제 완료"),
  failed: tt("a.086147a7", "실패"),
  canceled: tt("a.ff06fca3", "환불"),
  partial_canceled: tt("a.4c2b5969", "부분 환불"),
}));

const BAND_NAME = Object.freeze({ S: "마스터", A: "다이아몬드", B: "플래티넘", C: "골드", D: "실버", rookie: "루키" });

export function tierNameFromBand(band) {
  if (!band) return null;
  return BAND_NAME[band] || null;
}

// 가입비(첫 결제) — **300,000원 고정** (대표 2026-09-21 2차 결정). 결제일부터 한 달(같은 날짜 전날까지) 실버 등급 이용이 포함되고,
// 그 뒤 그달 남은 날은 그달 확정 등급 멤버십 구독료를 일할로 청구, 다음 달 1일부터 월 멤버십 구독료. 예: 10/20 결제 → 11/19 까지 포함 → 11/20~11/30 은 11월 확정 등급 × 11/30 일할 → 12/1 부터 월 멤버십 구독료.
// 서버 billing_signup_coverage(p_on) 와 같은 규칙 — 한쪽만 바꾸지 말 것. 금액은 서버가 정한다(여기서는 안내용).
function ymdUTC(d) { return d.toISOString().slice(0, 10); }
export function signupCoverage(fees, dateISO) {
  const amount = Number((fees || {}).signup_fee ?? 300000);
  const d = dateISO ? new Date(`${String(dateISO).slice(0, 10)}T00:00:00Z`) : null;
  if (!d || Number.isNaN(d.getTime())) return { amount, covers_from: null, covers_until: null, remainder_from: null, remainder_until: null, remainder_days: 0, days_in_month: null };
  const y = d.getUTCFullYear(); const m = d.getUTCMonth(); const day = d.getUTCDate();
  // 다음 달 같은 날짜(없으면 그달 말일)의 전날까지 포함
  const nextMonthLast = new Date(Date.UTC(y, m + 2, 0)).getUTCDate();
  const end = new Date(Date.UTC(y, m + 1, Math.min(day, nextMonthLast)));
  const coversUntil = new Date(end.getTime() - 86400000);
  const remainderFrom = end;
  const rmY = remainderFrom.getUTCFullYear(); const rmM = remainderFrom.getUTCMonth();
  const daysInMonth = new Date(Date.UTC(rmY, rmM + 1, 0)).getUTCDate();
  const remainderDays = remainderFrom.getUTCDate() === 1 ? 0 : daysInMonth - remainderFrom.getUTCDate() + 1;
  return {
    amount, covers_from: ymdUTC(d), covers_until: ymdUTC(coversUntil),
    remainder_from: remainderDays ? ymdUTC(remainderFrom) : null,
    remainder_until: remainderDays ? ymdUTC(new Date(Date.UTC(rmY, rmM + 1, 0))) : null,
    remainder_days: remainderDays, days_in_month: daysInMonth,
  };
}

// 청약철회 제한 사전 고지(전자상거래법 제17조 2항 · refund-policy-plan §5-1) — 결제 전 동의 체크 문구. 서버가 payment_order.ack.text / billing_event_log 에 그대로 남긴다.
// 「이용」 정의는 서버 billing_refund_window 와 같아야 한다(승인된 작품 · 다운로드 · 검수 신청 · 등급 승급권 사용).
export const REFUND_NOTICE_TEXT = "결제한 뒤 작품 사용 신청이 승인되거나 소재 다운로드·검수 신청을 시작하면 이 결제는 환불되지 않습니다. 결제일 포함 7일 안에 이용 없이 해지하면 전액 환불됩니다. 해지하면 이미 결제한 기간 끝까지 이용하고 그 뒤로 청구되지 않으며, 남은 기간의 일할 환불은 없습니다. 이 내용을 확인했고 동의합니다."; // 법적 동의·표시 원문 — 서버 기록·정확한 문구가 필요해 번역하지 않는다

// 이용약관 제26조의2(등급에 따른 작품 제공) 별도 동의 — 부칙 10: 시행일(2026-11-07) 전에 가입비를 결제하는 회원에게는 결제 화면에서
// 제26조의2 내용을 표시하고 별도 동의를 받는다. 시행일부터는 모든 회원에게 적용되므로 묻지 않는다.
// 동의 원문은 가입비 주문의 ack_text 뒤에 붙어 payment_order.ack.text 에 남는다(Edge 무변경 · 1,000자 안). 법적 문구라 번역하지 않는다.
export const TIER_WORKS_CONSENT_FROM = "2026-11-07";
export const TIER_WORKS_CONSENT_TEXT = "[이용약관 제26조의2 별도 동의] 가입비를 결제하면 내 등급에 제공되는 작품에 사용 신청할 수 있습니다. 작품마다 신청에 필요한 최소 등급(실버·골드·플래티넘·다이아몬드·마스터)이 정해져 있어, 내 등급보다 높은 최소 등급이 표시된 작품은 신청할 수 없고, 등급이 오르면 신청할 수 있는 작품이 늘어납니다. 이미 승인된 사용 신청은 등급이 바뀌어도 유지됩니다. 2026년 11월 7일 시행되는 이 조항을 결제 시점부터 저에게 적용하는 것에 동의합니다.";
/** 오늘(KST, YYYY-MM-DD)이 제26조의2 시행일 전이면 가입비 결제 화면에서 별도 동의를 받는다 */
export function needsTierWorksConsent(todayYmd) {
  const d = String(todayYmd || "").slice(0, 10);
  return /^\d{4}-\d{2}-\d{2}$/.test(d) && d < TIER_WORKS_CONSENT_FROM;
}
/** 가입비 주문에 남길 동의 원문 — 환불 안내 + (시행 전이면) 제26조의2 */
export function signupAckText(todayYmd) {
  return needsTierWorksConsent(todayYmd) ? `${REFUND_NOTICE_TEXT}\n${TIER_WORKS_CONSENT_TEXT}` : REFUND_NOTICE_TEXT;
}

// 해지 사유 — 해지가 **끝난 뒤** 선택형으로만 묻는다(2026-09-22). 코드는 서버 체크 제약(subscription_cancel_reason)과 같아야 한다.
// 🔒 해지 전에 묻는 필수 설문으로 바꾸지 말 것 — 전자상거래법 제21조의2(취소 방해).
export const CANCEL_REASONS = lazyI18n(() => Object.freeze([
  { code: "too_expensive", label: tt("a.4e2dbb29", "멤버십 구독료가 부담돼요") },
  { code: "not_using", label: tt("a.9d7995a2", "잘 안 쓰게 돼요") },
  { code: "no_works", label: tt("a.5f048a7c", "원하는 작품이 없어요") },
  { code: "hard_to_use", label: tt("a.7d6957e2", "쓰기 불편하거나 오류가 있었어요") },
  { code: "other", label: tt("a.97f6e862", "그 밖의 이유") },
]));

// 해지 확인창 문구 — refund-policy-plan §5-1 네 줄. overview.refund_window(서버 billing_refund_window) 가 안전창을 자동 판정한다.
// 종료일: 서버 billing_cancel_subscription 과 같은 규칙 — current_period_end 가 오늘보다 뒤면 그날, 아니면 다음 달 1일. 한쪽만 바꾸지 말 것.
export function cancelPreview(overview) {
  const sub = overview?.subscription || {};
  const today = overview?.today ? String(overview.today).slice(0, 10) : null;
  const fees = overview?.fees || {};
  let effective = null;
  const periodEnd = sub.current_period_end ? String(sub.current_period_end).slice(0, 10) : null;
  if (today) {
    if (periodEnd && periodEnd > today) effective = periodEnd;
    else {
      const [y, m] = today.split("-").map(Number);
      effective = ymdUTC(new Date(Date.UTC(y, m, 1)));
    }
  }
  const lastDay = effective ? ymdUTC(new Date(new Date(`${effective}T00:00:00Z`).getTime() - 86400000)) : null;
  const immediate = sub.status !== "active";   // 미납·결제수단 없음은 이번 달분을 내지 않았으므로 바로 중단
  const win = overview?.refund_window || {};
  return {
    immediate, effective_at: immediate ? null : effective, last_day: immediate ? null : lastDay,
    reactivation_fee: Number(overview?.reactivation?.amount ?? fees.reactivation_fee ?? 0),
    refund_eligible: !immediate && win.eligible === true,
    refund_amount: win.eligible === true ? Number(win.amount || 0) : 0,
    refund_deadline: win.deadline || null,
  };
}

// 149000 → "149,000원" · 0 → "0원" · null → ""
export function formatKrw(amount) {
  if (amount === null || amount === undefined || Number.isNaN(Number(amount))) return "";
  return tt("a.f7dda0b8", "{{v0}}원", { v0: Math.round(Number(amount)).toLocaleString("ko-KR") });
}

export function orderKindLabel(kind) {
  return ORDER_KIND_LABEL[kind] || kind || "";
}

function addDaysYmd(ymd, days) {
  const d = new Date(`${String(ymd).slice(0, 10)}T00:00:00Z`);
  if (Number.isNaN(d.getTime())) return ymd;
  return ymdUTC(new Date(d.getTime() + days * 86400000));
}

// "2026-10-01" → "10월 1일"
export function shortDateLabel(ymd) {
  if (!ymd) return "";
  const s = String(ymd).slice(0, 10);
  const [, m, d] = s.split("-").map(Number);
  if (!m || !d) return s;
  return tt("a.4ee7959e", "{{v0}}월 {{v1}}일", { v0: m, v1: d });
}

// next_charge_at(ISO) → "10월 1일 10:00" (KST)
export function nextChargeLabel(iso) {
  if (!iso) return "";
  const t = new Date(iso);
  if (Number.isNaN(t.getTime())) return "";
  const k = new Date(t.getTime() + 9 * 60 * 60 * 1000);
  const hh = String(k.getUTCHours()).padStart(2, "0");
  const mm = String(k.getUTCMinutes()).padStart(2, "0");
  return tt("a.9c85a0ef", "{{v0}}월 {{v1}}일 {{v2}}:{{v3}}", { v0: k.getUTCMonth() + 1, v1: k.getUTCDate(), v2: hh, v3: mm });
}

// 결제수단 표시 "신한카드 ···· 1234"
export function paymentMethodLabel(pm) {
  if (!pm) return tt("a.dc4d5f0b", "등록된 결제수단 없음");
  const company = cardCompanyName(pm.card_company, "카드");   // 옛 저장값(코드)도 이름으로
  return pm.number_masked ? `${company} ···· ${pm.number_masked}` : company;
}

// 「결제수단 등록」 할 일의 본문 — 등록 즉시 결제되는 금액. 이달 일할(데뷔 달 · 서버 this_month)이면 그 금액과 날짜를, 아니면 월 구독료 전액(종전 문장 그대로).
function registerBody(overview, tierName, price) {
  const pro = thisMonthProration(overview);
  return pro
    ? tt("sff.registerProrated", "{{v0}} 월 {{v1}}. 등록 즉시 이번 달분 {{v2}}({{v3}}부터 말일까지 {{v4}}/{{v5}}일분)이 결제되고, 다음 달 1일부터 월 멤버십 구독료가 결제됩니다.", {
      v0: tierName, v1: formatKrw(price), v2: formatKrw(pro.amount), v3: shortDateLabel(pro.from), v4: pro.days, v5: pro.daysInMonth,
    })
    : tt("a.cf5b3034", "{{v0}} 월 {{v1}}. 등록 즉시 이번 달분이 결제됩니다.", { v0: tierName, v1: formatKrw(price) });
}

/**
 * 「지금 할 일」 판정 — §5-3 우선순위. 위에서부터 첫 번째로 맞는 것 하나만 돌려준다.
 * @returns {{ key, title, body, actions: [{ label, to?, action?, variant? }] }}
 */
export function resolveTodo(overview) {
  if (!overview || overview.active === false) {
    return { key: "inactive", title: tt("a.f53f37de", "멤버십·결제는 준비 중입니다"), body: tt("a.71a6aa36", "시행되면 이 화면에서 멤버십 구독료와 결제를 관리할 수 있습니다."), actions: [] };
  }
  const sub = overview.subscription || {};
  const fees = overview.fees || {};
  const signup = overview.signup_fee || {};
  const react = overview.reactivation || {};
  const pc = overview.price_change;
  const exempt = overview.exempt || {};
  const price = Number(overview.monthly_price || 0);
  const tierName = overview.tier?.tier_name ? ttl(overview.tier.tier_name) : "";

  // 1. 가입비 미결제
  if (signup.required && !signup.paid_at) {
    const c = signupCoverage(fees, overview.today);
    // 2026-10-10 QA: 조각 세 개가 tt() 밖의 한국어 템플릿이라 영·일·중에서도 한국어로 남았다(Billing.i18n.test.jsx 가 지킨다)
    const cover = c.covers_until
      ? tt("billing.signup.coverUntil", " 결제일부터 {{v0}}까지 실버 등급 이용이 포함되고,", { v0: shortDateLabel(c.covers_until) })
      : tt("billing.signup.coverMonth", " 결제일부터 한 달 실버 등급 이용이 포함되고,");
    const rest = c.remainder_days
      ? tt("billing.signup.rest", " {{v0}}~{{v1}}은 그달 확정 등급 멤버십 구독료를 {{v2}}/{{v3}}일분만 청구합니다(면제 등급이면 없음).", {
        v0: shortDateLabel(c.remainder_from), v1: shortDateLabel(c.remainder_until), v2: c.remainder_days, v3: c.days_in_month,
      })
      : tt("billing.signup.restNext", " 그다음 달 1일부터 확정 등급 멤버십 구독료가 청구됩니다.");
    // 기한이 오늘이거나 지났다 = 지금 결제해야 이용 시작(시작 등급 지정은 기한 = 지정일 · 대표 지시 2026-10-20). 「○일까지」 대신 「결제하면 바로」.
    // 기한이 없으면(서버가 「대상인데 안 냄」을 읽기로 알려 준 경우 · 2026-10-23) 기한을 지어내지 않고 「지금 결제」로 — 「14일 안에」는 폐지된 옛 규칙(2026-10-20)
    const dueNow = !signup.due_at || Boolean(overview.today && String(signup.due_at).slice(0, 10) <= String(overview.today).slice(0, 10));
    return {
      key: "signup_fee",
      title: tt("a.f4881444", "가입비 {{v0}}을 결제하면 시작됩니다", { v0: formatKrw(c.amount) }),
      body: dueNow
        ? tt("tgp.signupBodyNow", "결제하면 바로 시작할 수 있어요.{{v0}}{{v1}} 결제 전에는 작품 신청·다운로드가 멈춰 있어요.", { v0: cover, v1: rest })
        : tt("a.4d24d846", "{{v0}}{{v1}}{{v2}} 기한이 지나면 작품 신청·다운로드가 정지됩니다.", {
          v0: tt("billing.signup.dueBy", "{{v0}}까지 결제해 주세요.", { v0: shortDateLabel(signup.due_at) }),   // 기한이 없으면 위 dueNow 분기(「14일 안에」 옛 문구는 쓰지 않는다)
          v1: cover, v2: rest,
        }),
      actions: [{ label: tt("a.e53fa159", "가입비 결제하기"), to: "/billing/pay?kind=signup_fee" }],
    };
  }
  // 2. 휴면 · 재활성화 필요
  if (sub.status === "paused" || (react.required && !react.paid_at)) {
    // 기존 회원은 청구 시작일 전날까지 재활성화비 면제(대표 2026-10-13 · 시안 C) — 서버 billing_overview.reactivation.waived
    if (react.waived) {
      return {
        key: "reactivation",
        title: tt("a.dw.billingTitle", "기존 회원 혜택 · {{v0}}까지는 재활성화비 없이 다시 시작할 수 있어요", { v0: shortDateLabel(react.waived_until) }),
        body: (sub.status === "paused" ? tt("a.7153089e", "6개월간 작품 협업이 없어 계정이 휴면으로 전환됐습니다.") + " " : "")
          + tt("a.dw.billingBody", "{{v0}}까지 돌아오면 재활성화비를 내지 않아요. 그 뒤에는 {{v1}}이 1회 있어요.", { v0: shortDateLabel(react.waived_until), v1: formatKrw(react.list_amount ?? fees.reactivation_fee) }),
        actions: [{ label: tt("a.dw.billingCta", "무료로 다시 시작"), to: "/billing/pay?kind=reactivation_fee" }],
      };
    }
    return {
      key: "reactivation",
      title: reactivationBundleOn(fees, overview?.today)
        ? tt("a.248b9eb9", "재활성화비 {{v0}}와 이번 달 멤버십 구독료를 함께 결제하면 재개됩니다", { v0: formatKrw(react.amount ?? fees.reactivation_fee) })
        : tt("a.23de0bab", "재활성화비 {{v0}} 결제 시 이번 달 확정 등급으로 재개됩니다", { v0: formatKrw(react.amount ?? fees.reactivation_fee) }),
      body: sub.status === "paused" ? tt("a.7153089e", "6개월간 작품 협업이 없어 계정이 휴면으로 전환됐습니다.") : tt("a.b740b79f", "멤버십이 중단된 상태입니다. 해지·중단 후 재개에는 재활성화비가 필요합니다."),
      actions: [{ label: tt("a.df24c650", "재활성화비 결제하기"), to: "/billing/pay?kind=reactivation_fee" }],
    };
  }
  // 3. 멤버십 구독료 변경 동의 대기
  if (pc && pc.decided === "pending") {
    return {
      key: "price_change",
      title: tt("a.7105f5e4", "{{v0}}로 확정 · 이번 달부터 월 {{v1}}", { v0: pc.to_tier_name || tierNameFromBand(pc.to_band) || "", v1: formatKrw(pc.to_price) }),
      body: tt("a.569eb067", "이전 {{v0}} → {{v1}}. 동의하면 그 자리에서 결제됩니다. 중단을 고르면 재개 시 재활성화비가 필요합니다.", { v0: formatKrw(pc.from_price), v1: formatKrw(pc.to_price) }),
      actions: [
        { label: tt("a.414e4e61", "동의하고 결제"), action: "consent_price_change" },
        { label: tt("a.cc288e58", "멤버십 중단"), action: "decline_price_change", variant: "outline" },   // 2026-09-22 동의/거부 버튼 시각 동등(전자상거래법 제21조의2)
      ],
    };
  }
  // 3-1. 기존 회원 유료 전환 미동의 — 동의 전에는 청구도 이용도 멈춘다(서버 can_use_works · chargeUser 와 짝). 10월 동의 창을 놓친 사람이 여기서 동의한다.
  if (overview.paid_consent_required) {
    return {
      key: "paid_consent",
      title: tt("a.4d483f47", "멤버십 구독료 시작에 동의해 주세요 · {{v0}} 월 {{v1}}", { v0: tierName, v1: formatKrw(price) }),
      body: tt("a.6acd1435", "동의 전에는 청구하지 않으며, 신규 작품 사용 신청과 소재 다운로드가 잠시 멈춥니다. 이미 승인된 영상의 정산은 계속됩니다. 동의하면 바로 재개됩니다."),
      actions: [{ label: tt("a.0aa188fd", "내용 확인하고 동의"), action: "consent_paid_change" }],
    };
  }
  // 4. 결제수단 없음 (카드 전용 — 청구서 모드는 2026-09-21 폐기)
  if (!exempt.exempt && price > 0 && sub.status === "none" && !overview.payment_method) {
    return {
      key: "register_method",
      title: tt("a.50a60a70", "결제수단을 등록해 주세요{{v0}}", { v0: sub.method_register_due_at ? ` · ${shortDateLabel(sub.method_register_due_at)}까지` : "" }),
      body: registerBody(overview, tierName, price),
      actions: [{ label: tt("a.d8c83fab", "결제수단 등록"), action: "register_method" }],
    };
  }
  // 4-1. 결제는 했는데(가입비·재활성화 → status active) 자동 결제할 카드가 없다 — 이미 결제한 기간이 끝나면 청구할 수단이 없어
  //      잔여일·월 멤버십 구독료가 빠진다(대표 2026-10-20 「잔여일·월 구독료가 빠지지 않게」). 카드를 등록해도 이미 결제한 기간에는
  //      청구하지 않고 그 끝날 10:00 로 예약된다(_shared/charge.ts chargeUser 「period_covered」).
  if (!exempt.exempt && price > 0 && sub.status === "active" && !overview.payment_method) {
    const pe = coveredPeriodEnd(overview);
    return {
      key: "register_method",
      title: tt("a.50a60a70", "결제수단을 등록해 주세요{{v0}}", { v0: "" }),
      body: pe
        ? tt("tgp.todoCardCovered", "이미 결제한 기간은 {{v0}}까지예요. 카드를 등록해 두면 {{v1}}부터 {{v2}} 월 {{v3}} 기준으로 자동 결제돼요(그달 남은 날은 날짜만큼). 등록하는 날에는 결제되지 않아요.", {
          v0: shortDateLabel(addDaysYmd(pe, -1)), v1: shortDateLabel(pe), v2: tierName, v3: formatKrw(price),
        })
        : registerBody(overview, tierName, price),
      actions: [{ label: tt("a.d8c83fab", "결제수단 등록"), action: "register_method" }],
    };
  }
  // 5. 미납
  if (sub.status === "past_due") {
    return {
      key: "past_due",
      title: tt("a.9260f4e5", "멤버십을 계속할까요?"),
      body: tt("a.faad36c0", "{{v0}}일째 결제되지 않았습니다. 말일까지 미결제면 멤버십이 중단되고, 재개에는 재활성화비가 필요합니다.", { v0: sub.retry_count || 0 }),
      actions: [
        { label: tt("a.63fb7d92", "결제수단 바꾸고 결제"), action: "register_method" },
        { label: tt("a.6abb2075", "오늘 다시 시도"), action: "charge_now", variant: "outline" },
        { label: tt("a.cc288e58", "멤버십 중단"), action: "cancel_subscription", variant: "ghost" },
      ],
    };
  }
  // 5-1. 해지 예약 — 종료일 전날까지 이용, 되돌리기 무료 (R1)
  if (sub.status === "cancel_at_period_end") {
    const eff = sub.cancel_effective_at ? String(sub.cancel_effective_at).slice(0, 10) : null;
    const last = eff ? ymdUTC(new Date(new Date(`${eff}T00:00:00Z`).getTime() - 86400000)) : null;
    const rr = overview.refund_request;
    // 자동 환불이 끝나지 못한 경우(토스 취소 실패 등)에만 남는다 — 운영팀이 환불 탭에서 처리
    const refundNote = rr && (rr.status === "requested" || rr.status === "failed") ? " " + tt("a.bcb96b67", "환불 요청({{v0}})을 접수했습니다 — 운영팀이 확인해 3영업일 안에 처리합니다.", { v0: formatKrw(rr.amount) }) : "";
    return {
      key: "cancel_reserved",
      title: eff ? tt("a.685b5d75", "{{v0}}부터 청구 없음 · {{v1}}까지 이용", { v0: shortDateLabel(eff), v1: shortDateLabel(last) }) : tt("a.f4ffeca8", "멤버십 해지를 예약했습니다"),
      body: tt("a.d2cbd7e3", "남은 기간 환불은 없습니다. 종료 뒤 다시 시작하려면 재활성화비 {{v0}}{{v1}}가 필요합니다. 그 전에 해지를 취소하면 비용 없이 그대로 이어집니다.{{v2}}", { v0: formatKrw(react.list_amount ?? react.amount ?? fees.reactivation_fee), v1: reactivationBundleOn(fees, overview?.today) ? "와 그달 멤버십 구독료(일할)" : "", v2: refundNote }),
      actions: [{ label: tt("a.338791be", "해지 취소"), action: "undo_cancel" }],
    };
  }
  // 6. 중단됨
  if (sub.status === "canceled") {
    if (react.waived) {
      return {
        key: "canceled",
        title: tt("a.dw.billingTitle", "기존 회원 혜택 · {{v0}}까지는 재활성화비 없이 다시 시작할 수 있어요", { v0: shortDateLabel(react.waived_until) }),
        body: tt("a.dw.billingBody", "{{v0}}까지 돌아오면 재활성화비를 내지 않아요. 그 뒤에는 {{v1}}이 1회 있어요.", { v0: shortDateLabel(react.waived_until), v1: formatKrw(react.list_amount ?? fees.reactivation_fee) }),
        actions: [{ label: tt("a.dw.billingCta", "무료로 다시 시작"), to: "/billing/pay?kind=reactivation_fee" }],
      };
    }
    return {
      key: "canceled",
      title: reactivationBundleOn(fees, overview?.today)
        ? tt("a.53937e37", "다시 시작하려면 재활성화비 {{v0}}와 그달 멤버십 구독료(일할) 결제가 필요합니다", { v0: formatKrw(react.amount ?? fees.reactivation_fee) })
        : tt("a.0846b0b7", "다시 시작하려면 재활성화비 {{v0}} 결제가 필요합니다", { v0: formatKrw(react.amount ?? fees.reactivation_fee) }),
      body: tt("a.dff3985e", "해지·중단 후 재개에는 유예 기간이 없습니다."),
      actions: [{ label: tt("a.265fae40", "다시 시작"), to: "/billing/pay?kind=reactivation_fee" }],
    };
  }
  // 7. 면제
  if (exempt.exempt || price === 0) {
    const reason = {
      admin: tt("a.1c83f14c", "운영 계정은 멤버십 구독료가 청구되지 않습니다."),
      tier_free: tt("a.26b47dd8", "{{v0}} 등급은 멤버십 구독료가 면제됩니다. 등급이 내려가면 미리 알려 드립니다.", { v0: tierName || "다이아몬드 이상" }),
      rookie: tt("a.44c5126e", "루키는 정식 데뷔 달부터 멤버십 구독료가 청구됩니다."),
      dormant: tt("a.bf4f3b49", "휴면 등급은 멤버십 구독료가 청구되지 않습니다."),
      grace: exempt.until ? tt("a.9dda22d8", "기존 회원은 {{v0}}까지 멤버십 구독료가 유예됩니다. {{v1}}부터 그달 확정 등급 멤버십 구독료가 청구됩니다.", { v0: shortDateLabel(addDaysYmd(exempt.until, -1)), v1: shortDateLabel(exempt.until) }) : tt("a.ee077980", "멤버십 구독료 청구 시작일이 아직 정해지지 않았습니다."),
      manual: tt("a.1dc15de1", "수동 면제 중입니다{{v0}}.", { v0: sub.manual_exempt_until ? tt("a.b4a77927", " ({{v0}}까지)", { v0: shortDateLabel(sub.manual_exempt_until) }) : "" }),
      trial: tt("a.67bd8ad9", "무료 기간입니다{{v0}}.", { v0: sub.trial_until ? tt("a.b4a77927", " ({{v0}}까지)", { v0: shortDateLabel(sub.trial_until) }) : "" }),
      // 추천 가입 혜택(가입 유형 「멤버십 구독료 면제 N개월」 · 2026-10-06) — until = 마지막 무료일, resumes_on = 청구가 다시 시작되는 날(그달 남은 날 일할)
      promo: exempt.until
        ? tt("a.9ec4574b", "추천 가입 혜택으로 {{v0}}까지 멤버십 구독료가 없어요. {{v1}}부터 그달 남은 날은 날짜만큼, 다음 달부터 월 멤버십 구독료가 청구됩니다.", { v0: shortDateLabel(exempt.until), v1: shortDateLabel(exempt.resumes_on || addDaysYmd(exempt.until, 1)) })
        : tt("a.9440a783", "추천 가입 혜택으로 지금은 멤버십 구독료가 없어요."),
    }[exempt.reason] || tt("a.30ca8214", "이번 달은 청구되지 않습니다.");
    const title = exempt.reason === "grace" ? tt("a.10c0ac3e", "지금은 청구 없음 · 멤버십 구독료 유예 중")
      : exempt.reason === "promo" ? tt("a.56a679eb", "청구 없음 · 추천 가입 혜택")
      : tt("a.eaadea1d", "청구 없음 · 내려가면 미리 알림");
    return { key: "exempt", title, body: reason, actions: [] };
  }
  // 8. 정상
  const nextPro = nextChargeProration(overview);
  return {
    key: "normal",
    title: sub.next_charge_at ? tt("a.1ba0d76a", "다음 결제 {{v0}} · {{v1}}", { v0: nextChargeLabel(sub.next_charge_at), v1: formatKrw(nextPro ? nextPro.amount : price) }) : tt("a.1bc52a8c", "{{v0}} 월 {{v1}}", { v0: tierName, v1: formatKrw(price) }),
    body: nextPro
      ? tt("bsf.nextProratedBody", "{{v0}}로 자동 결제됩니다. {{v1}}부터 말일까지 {{v2}}/{{v3}}일분(월 {{v4}})이고, 다음 달 1일부터 월 멤버십 구독료가 결제됩니다.", { v0: paymentMethodLabel(overview.payment_method), v1: shortDateLabel(nextPro.from), v2: nextPro.days, v3: nextPro.daysInMonth, v4: formatKrw(price) })
      : tt("a.e6e625c5", "{{v0}}로 자동 결제됩니다.", { v0: paymentMethodLabel(overview.payment_method) }),
    actions: overview.access_pass?.eligible && !overview.access_pass?.id ? [{ label: tt("a.ace645f3", "등급 승급권 알아보기"), action: "access_pass_info", variant: "outline" }] : [],  // 2026-09-22 대표 결정: 승급권은 사면 진짜 그 등급이 된다 · 총 20석 한정. 결제 전 환불 불가 동의 필수
  };
}

// 이벤트 → 날짜 띠(31칸) + 목록. events 는 서버가 만든 배열(day 0/-1 = 전월).
export function buildTimeline(overview) {
  const today = overview?.today ? Number(String(overview.today).slice(8, 10)) : null;
  const monthStr = overview?.month ? String(overview.month).slice(0, 7) : "";
  const [y, m] = monthStr ? monthStr.split("-").map(Number) : [null, null];
  const daysInMonth = y && m ? new Date(y, m, 0).getDate() : 31;
  const events = Array.isArray(overview?.events) ? overview.events.slice() : [];
  const order = { pre: 0, done: 1, ok: 1, bad: 1, now: 2, future: 3 };
  events.sort((a, b) => (Number(a.day) - Number(b.day)) || ((order[a.state] ?? 9) - (order[b.state] ?? 9)));
  const cells = [];
  for (let d = 1; d <= daysInMonth; d += 1) {
    const dayEvents = events.filter((e) => Number(e.day) === d);
    let state = "plain";
    if (dayEvents.some((e) => e.state === "bad")) state = "bad";
    else if (dayEvents.some((e) => e.state === "ok")) state = "ok";
    else if (dayEvents.some((e) => e.state === "now")) state = "now";
    else if (dayEvents.some((e) => e.state === "future")) state = "future";
    else if (dayEvents.length) state = "done";
    if (today && d === today && state === "plain") state = "today";
    cells.push({ day: d, state, isToday: today === d, isPast: today ? d < today : false, events: dayEvents });
  }
  const pre = events.filter((e) => Number(e.day) <= 0).map((e) => ({ ...e, label_day: Number(e.day) === 0 ? tt("a.6cd21c8c", "전월 25일") : tt("a.f40bf131", "전월 말일") }));
  const cur = events.filter((e) => Number(e.day) > 0).map((e) => ({ ...e, label_day: tt("a.3a8c3145", "{{v0}}일", { v0: e.day }) }));
  return { monthLabel: m ? tt("a.831d8090", "{{v0}}월", { v0: m }) : "", daysInMonth, today, cells, list: [...pre, ...cur] };
}

// 서버 오류 → 결제 게이트 여부 (apply_work_usage 의 hint BILLING_REQUIRED)
export function isBillingRequiredError(error) {
  if (!error) return false;
  const hint = String(error.hint || "");
  const msg = String(error.message || "");
  return hint === "BILLING_REQUIRED" || /billing required/i.test(msg);
}

// ── 메뉴 배지 · 홈 띠 (2026-09-22 대표 결정 「멤버십 메뉴는 권고안대로」 — 시안 docs/design/billing-sheet-copy-variants.html) ──
// 멤버십은 상단 메뉴에 넣지 않는다. 사이드바 「계정 › 멤버십」·프로필 메뉴 「멤버십」에 상태 배지, 홈 맨 위에는 **할 일이 있을 때만** 띠.
// 문장은 전부 resolveTodo 에서 온다 — 여기서 새 문장을 만들지 않는다.
export const HOME_STRIP_TODO_KEYS = ["signup_fee", "reactivation", "price_change", "paid_consent", "register_method", "past_due", "cancel_reserved", "canceled"];

/** 메뉴 옆 작은 배지. 시행 전·조회 실패면 null. tone: brand | warning | error | neutral */
export function billingBadge(overview) {
  if (!overview || overview.active === false) return null;
  const sub = overview.subscription || {};
  const key = resolveTodo(overview).key;
  if (key === "past_due") return { label: tt("a.e8f5ebd6", "미납"), tone: "error" };
  if (key === "cancel_reserved") return { label: tt("a.34e62dde", "해지 예약"), tone: "warning" };
  if (["signup_fee", "register_method", "paid_consent", "price_change", "reactivation"].includes(key)) return { label: tt("a.6fd5d5ca", "할 일"), tone: "warning" };
  if (sub.status === "paused" || sub.status === "canceled") return { label: tt("a.46456178", "중단"), tone: "neutral" };
  if (overview.exempt?.exempt) return { label: tt("a.acd64428", "면제"), tone: "neutral" };
  if (sub.status === "active") return { label: tt("a.3fa4b276", "구독 중"), tone: "brand" };
  return null;
}

/** 홈 최상단 띠 — 할 일이 있을 때만. { key, title, cta: { label, to } } | null */
export function homeStripCopy(overview) {
  if (!overview || overview.active === false) return null;
  const todo = resolveTodo(overview);
  if (!HOME_STRIP_TODO_KEYS.includes(todo.key)) return null;
  const first = todo.actions?.[0];
  return { key: todo.key, title: todo.title, cta: { label: first?.label || tt("a.8668ff7d", "멤버십 화면 열기"), to: "/billing" } };
}

// ── 정기결제 동의 (2026-09-22 대표 결정 「리서치 결과 반영」 — docs/ops/billing-consent-research-2026-09-22.md §F) ──
// 전자상거래법 제13조②(가격·지급방법·지급시기·청약철회 조건)·제21조의2(다크패턴: 총액 한 화면·사전 체크 금지)·콘텐츠이용자보호지침 제25조(자동결제 금액·시기·방법 고지).
// 화면은 요약 5줄(recurringConsentSummary) + 체크 한 줄(CONSENT_CHECK_LABEL) + 「자세히」 전문(RECURRING_CONSENT_TEXT). 서버에 남기는 동의 원문 = 전문.
// 🔒 전문을 바꾸면 billing_event_log refund_notice_ack 에 새 원문이 남는다. 「이용」 정의(승인·다운로드·검수 신청)는 서버 billing_refund_window 와 같아야 한다.
export const COMPANY_IDENTITY_LINE = "(주)루나르트 · 대표 권재의 · 서울특별시 성동구 연무장5가길 25, 8층 804호 · 사업자등록번호 652-88-00793 · 통신판매업 신고 2024-서울광진-0001 · contact@laeebly.com";

export const CONSENT_CHECK_LABEL = "위 정기결제 조건(금액·결제일·자동 갱신·해지·환불)을 확인했고 동의합니다"; // 법적 동의·표시 원문 — 서버 기록·정확한 문구가 필요해 번역하지 않는다

export const RECURRING_CONSENT_TEXT = "(주)루나르트(대표 권재의, 서울특별시 성동구 연무장5가길 25, 8층 804호, 사업자등록번호 652-88-00793, 통신판매업 신고 2024-서울광진-0001, contact@laeebly.com)는 회원이 등록한 신용·체크카드로 매월 1일 10:00(KST)에 해당 월 등급 멤버십 구독료를 자동 결제합니다. 멤버십 구독료는 등급별로 실버 199,000원·골드 99,000원·플래티넘 55,000원(부가가치세 포함)이며 다이아몬드·마스터·루키는 청구하지 않습니다. 등급이 바뀌어 멤버십 구독료가 오르는 경우 인상 멤버십 구독료를 청구하기 전에 안내하고 회원의 별도 동의를 받은 뒤에만 청구하며, 동의하지 않으면 청구하지 않고 멤버십은 중단됩니다. 매 결제 7일 전 앱 알림과 이메일로 결제 금액·결제일·해지 방법을 알려 드립니다. 결제가 실패하면 이용약관 제17조에 따라 매일 다시 시도하며 말일까지 미결제면 멤버십이 중단됩니다. 해지는 웹 멤버십 화면의 「멤버십 해지」에서 24시간 언제든 할 수 있고 가입보다 많은 단계를 요구하지 않습니다. 해지하면 이미 결제한 기간 끝까지 이용할 수 있고 다음 달부터 청구되지 않으며, 남은 기간의 일할 환불은 없습니다. 결제일 포함 7일 안에 작품 사용 신청 승인·소재 다운로드·검수 신청 없이 해지하면 전액 환불하며, 그 이후에는 디지털 콘텐츠 제공이 시작된 것으로 보아 환불되지 않습니다(전자상거래법 제17조). 이용 전에는 작품 정보와 미리보기로 서비스 내용을 확인할 수 있습니다. 환불은 결제한 카드로 처리하며 증빙은 카드 매출전표로 제공합니다(세금계산서는 발급하지 않습니다). 자세한 내용은 이용약관 제16·17·20·21·24조를 따릅니다. 이 내용을 확인했고 동의합니다."; // 법적 동의·표시 원문 — 서버 기록·정확한 문구가 필요해 번역하지 않는다

/** 결제수단 등록 창 요약 5줄 — 자동결제 사실 · 오늘 결제 · 다음 결제/7일 전 알림 · 해지 방법 · 환불 기준 */
/**
 * 이미 결제한 기간(가입비 포함 기간 · 재활성화 달)이 오늘 뒤에 끝나면 그 다음 날(= 청구가 시작되는 날, YYYY-MM-DD). 아니면 null.
 * 서버 chargeUser 와 같은 기준 — current_period_end > 오늘이면 카드를 등록해도 청구하지 않고 그날 10:00 로 예약한다(2026-10-20).
 */
export function coveredPeriodEnd(overview) {
  const pe = overview?.subscription?.current_period_end ? String(overview.subscription.current_period_end).slice(0, 10) : null;
  const today = overview?.today ? String(overview.today).slice(0, 10) : null;
  return pe && today && pe > today ? pe : null;
}
const kstTenAmIso = (ymd) => `${ymd}T01:00:00.000Z`;   // KST 10:00 = UTC 01:00

/**
 * 이달 청구 금액이 월 구독료와 다른가(일할) — 서버 billing_overview.this_month(= billing_charge_preview · 20261118100000) 를 그대로 읽는다. 화면에서 계산하지 않는다.
 * 데뷔(승급 심사 승인) 달은 승인일부터, 가입비·재활성화 포함 기간이 이달 중간에 끝나면 그날부터, 추천 가입 혜택이 끝난 다음 날부터 말일까지 날짜만큼.
 * Edge chargeUser 가 같은 함수로 청구하므로 「오늘 결제 N원」= 실제로 나가는 금액. 서버가 this_month 를 안 주면(구버전) null → 종전처럼 월 구독료.
 * @returns {{ amount: number, from: string, days: number, daysInMonth: number, reason: string } | null}
 */
export function thisMonthProration(overview) {
  const tm = overview?.this_month;
  if (!tm || !tm.prorated) return null;
  const amount = Number(tm.amount);
  const days = Number(tm.prorated_days);
  const daysInMonth = Number(tm.days_in_month);
  if (!Number.isFinite(amount) || amount <= 0 || !days || !daysInMonth) return null;
  return { amount, from: String(tm.prorated_from || "").slice(0, 10), days, daysInMonth, reason: String(tm.reason || "") };
}

/**
 * 다음 결제가 이미 결제한 기간(가입비 포함 기간 등)이 끝나는 날이고 그날이 1일이 아니면, 그달 말일까지 날짜만큼만 결제된다.
 * Edge chargeUser 와 같은 식 — 포함 기간 끝(current_period_end)이 그달 2일~말일이면 월 구독료 × 남은 일수 ÷ 그달 일수(원 미만 내림).
 * 화면이 「다음 결제 … 199,000원」으로 전액을 보이면 실제 결제(일할)와 어긋난다(2026-10-28 실서비스 확인).
 * @returns {{ amount: number, from: string, days: number, daysInMonth: number } | null}
 */
export function nextChargeProration(overview) {
  const sub = overview?.subscription || {};
  const price = Number(overview?.monthly_price || 0);
  if (!sub.next_charge_at || !(price > 0)) return null;
  const pe = sub.current_period_end ? String(sub.current_period_end).slice(0, 10) : null;
  const t = new Date(sub.next_charge_at);
  if (!pe || Number.isNaN(t.getTime())) return null;
  const chargeDay = new Date(t.getTime() + 9 * 60 * 60 * 1000).toISOString().slice(0, 10);
  if (chargeDay !== pe || pe.slice(8, 10) === "01") return null;
  const [y, m] = pe.split("-").map(Number);
  const daysInMonth = new Date(Date.UTC(y, m, 0)).getUTCDate();
  const days = daysInMonth - Number(pe.slice(8, 10)) + 1;
  return { amount: Math.floor((price * days) / daysInMonth), from: pe, days, daysInMonth };
}

export function recurringConsentSummary(overview) {
  const price = Number(overview?.monthly_price || 0);
  const tierName = overview?.tier?.tier_name ? ttl(overview.tier.tier_name) : "";
  const sub = overview?.subscription || {};
  const exempt = Boolean(overview?.exempt?.exempt);
  const covered = coveredPeriodEnd(overview);
  const pro = exempt || covered ? null : thisMonthProration(overview);   // 이달 일할(데뷔 달 등) — 서버 this_month 그대로
  const next = covered ? nextChargeLabel(kstTenAmIso(covered)) : sub.next_charge_at ? nextChargeLabel(sub.next_charge_at) : tt("a.e1eb7226", "매월 1일 10:00");
  return [
    { icon: "ok", title: tt("a.4f36b93c", "매월 1일 10:00 등록한 카드로 {{v0}}월 멤버십 구독료{{v1}}(VAT 포함)이 자동 결제돼요", { v0: tierName ? `${tierName} ` : "", v1: price > 0 ? ` ${formatKrw(price)}` : "" }), sub: tt("a.c49b53b5", "해지하기 전까지 매달 계속돼요. 등급이 내려가 멤버십 구독료가 오르면 별도 동의를 받은 뒤에만 청구해요") },
    { icon: exempt || price === 0 || covered ? "info" : "ok", title: exempt ? tt("a.0e7bc037", "오늘 결제 0원 · 지금은 면제라 청구되지 않아요") : covered ? tt("tgp.sumTodayCovered", "오늘 결제 0원 · 이미 결제한 기간이 {{v0}}까지예요", { v0: shortDateLabel(addDaysYmd(covered, -1)) }) : pro ? tt("sff.todayProrated", "오늘 결제 {{v0}} · {{v1}}부터 말일까지 {{v2}}/{{v3}}일분", { v0: formatKrw(pro.amount), v1: shortDateLabel(pro.from), v2: pro.days, v3: pro.daysInMonth }) : tt("a.bedb7e08", "오늘 결제 {{v0}} · 카드 등록 즉시 이번 달분", { v0: formatKrw(price) }), sub: exempt ? tt("a.83473a6e", "면제가 끝나는 달 1일부터 등급 멤버십 구독료가 청구돼요") : covered ? tt("tgp.sumTodayCoveredSub", "{{v0}}부터 그달 남은 날은 날짜만큼, 다음 달 1일부터 월 멤버십 구독료가 결제돼요", { v0: shortDateLabel(covered) }) : pro ? tt("sff.todayProratedSub", "이번 달은 월 {{v0}} 기준 날짜만큼만 · 다음 달 1일부터 월 멤버십 구독료", { v0: formatKrw(price) }) : tt("a.aed55f6a", "가입비·승급권 같은 1회 결제는 별도 화면에서 금액을 확인하고 결제해요") },
    { icon: "info", title: exempt ? tt("a.9b050f6f", "면제가 끝나면 매월 1일 10:00 · 결제 7일 전 알림") : covered && covered.slice(8, 10) !== "01" ? tt("tgp.sumNextCovered", "다음 결제 {{v0}} · 그달 남은 날만큼 일할 · 결제 7일 전 알림", { v0: next }) : tt("a.c473597c", "다음 결제 {{v0}} · {{v1}} · 결제 7일 전 알림", { v0: next, v1: formatKrw(price) }), sub: tt("a.913704b5", "결제 7일 전 앱 알림·이메일로 금액·결제일·해지 방법을 알려 드려요") },
    { icon: "info", title: tt("a.ee79e501", "해지는 멤버십 화면의 「멤버십 해지」에서 언제든 2단계"), sub: tt("a.d4582c49", "해지하면 결제한 기간 끝까지 쓰고 다음 달부터 청구 없음 · 남은 날 일할 환불 없음 · 종료 전엔 무료로 해지 취소") },
    { icon: "no", title: tt("a.1bc0cf29", "결제일 포함 7일 안에 이용 없이 해지하면 전액 환불 · 이용을 시작하면 환불 없음"), sub: tt("a.7a7a7587", "「이용」 = 작품 사용 신청 승인 · 소재 다운로드 · 검수 신청. 카드만 · 증빙은 카드 매출전표") },
  ];
}

// 결제수단 등록 창(시안 A 「숫자 먼저」, 대표 2026-09-22) — 금액 한 줄 + 요약 5줄(recurringConsentSummary) + 짧은 체크.
// 체크 문구는 CONSENT_CHECK_LABEL, 전문은 「자세히」로 펼친다. 서버에 남기는 동의 원문은 RECURRING_CONSENT_TEXT 다.
export function paymentSheetCopy(overview) {
  const price = Number(overview?.monthly_price || 0);
  const tierName = overview?.tier?.tier_name ? ttl(overview.tier.tier_name) : "";
  const sub = overview?.subscription || {};
  const exempt = Boolean(overview?.exempt?.exempt);
  // 이미 결제한 기간 안(가입비 포함 기간 등)이면 등록하는 날 청구가 없다 — 서버 chargeUser 「period_covered」 (2026-10-20)
  const covered = coveredPeriodEnd(overview);
  const pro = exempt || covered ? null : thisMonthProration(overview);   // 이달 일할(데뷔 달 등) — 서버 this_month 그대로 · chargeUser 와 같은 금액
  const todayAmount = pro ? pro.amount : price;
  const next = covered ? nextChargeLabel(kstTenAmIso(covered)) : sub.next_charge_at ? nextChargeLabel(sub.next_charge_at) : tt("a.e1eb7226", "매월 1일 10:00");
  return {
    amount: exempt ? tt("a.4ed20aeb", "0원") : formatKrw(todayAmount),
    amountSub: exempt ? tt("a.856de936", "{{v0}} · 지금은 면제라 청구되지 않아요", { v0: tierName })
      : covered ? tt("tgp.sheetCovered", "{{v0}} · 이미 결제한 기간이 끝나는 {{v1}}부터 결제", { v0: tierName, v1: shortDateLabel(covered) })
        : pro ? tt("sff.sheetProrated", "{{v0}} · 이번 달은 {{v1}}부터 {{v2}}/{{v3}}일분만(월 {{v4}})", { v0: tierName, v1: shortDateLabel(pro.from), v2: pro.days, v3: pro.daysInMonth, v4: formatKrw(price) })
          : tt("a.4cb87b66", "{{v0}} · 카드 등록 즉시 이번 달분 결제", { v0: tierName }),
    nextLabel: exempt ? tt("a.ded2e2b3", "면제가 끝나면 매월 1일 10:00") : tt("a.b8f7fd90", "다음은 {{v0}}", { v0: next }),
    rules: recurringConsentSummary(overview),
    agree: CONSENT_CHECK_LABEL,
    fullText: RECURRING_CONSENT_TEXT,
    company: COMPANY_IDENTITY_LINE,
    button: exempt || price === 0 || covered ? tt("a.852f0e0e", "카드 등록하기") : tt("a.454072c7", "카드 등록하고 {{v0}} 결제", { v0: formatKrw(todayAmount) }),
  };
}

// 재활성화 묶음 청구 시행 여부 — app_config.billing_fees.reactivation_bundle_from (약관 부칙 8 · 2026-10-23 시행).
// 🔒 시행 전에는 화면이 「멤버십 구독료도 함께」라고 말하면 안 된다 — 서버가 그때는 재활성화비만 청구한다.
export function reactivationBundleOn(fees, today) {
  const from = fees?.reactivation_bundle_from ? String(fees.reactivation_bundle_from).slice(0, 10) : null;
  if (!from) return false;
  const day = today ? String(today).slice(0, 10) : null;
  return Boolean(day && day >= from);
}

// ── 멤버십 구독료 사다리(PlanLadder) 금액 표기 ────────────────────────────────────────
// 🔒 금액을 숨기지 않는다 — **제대로 된 금액**을 보여 준다(대표 2026-09-23). 다만 유예·면제 중인 회원의
//    「내 등급」 줄은 청구되지 않는 금액이므로 **「면제」**라고 말한다. 기존 회원은 2026-10-31 까지 유예(grace).
//    다른 등급 줄은 제도 안내이므로 금액을 그대로 둔다.
export function ladderPriceLabel(row, { currentBand, exempt } = {}) {
  const price = Number(row?.monthly_price || 0);
  if (price === 0) return tt("a.acd64428", "면제");
  const mine = Boolean(row?.band && currentBand && row.band === currentBand);
  if (mine && exempt?.exempt) return tt("a.acd64428", "면제");
  return formatKrw(price);
}

// 사다리 아래 한 줄 — 왜 내 등급이 「면제」인지. 유예(grace)만 날짜를 말한다.
export function ladderExemptNote(exempt) {
  if (!exempt?.exempt) return null;
  if (exempt.reason === "grace") {
    return exempt.until
      ? tt("a.3a009fcc", "기존 회원은 {{v0}}까지 멤버십 구독료가 유예돼요. {{v1}}부터 내 등급 멤버십 구독료가 청구됩니다.", { v0: shortDateLabel(addDaysYmd(exempt.until, -1)), v1: shortDateLabel(exempt.until) })
      : tt("a.37988871", "기존 회원은 지금 멤버십 구독료가 유예 중이에요.");
  }
  if (exempt.reason === "rookie") return tt("a.1f158ccf", "루키는 멤버십 구독료가 없어요.");
  if (exempt.reason === "tier_free") return tt("a.a4c663fb", "이 등급은 멤버십 구독료가 없어요.");
  if (exempt.reason === "dormant") return tt("a.c1648b0f", "휴면 중에는 청구되지 않아요.");
  // 추천 가입 혜택 · 운영팀 면제 · 무료 기간 — 서버가 until(마지막 무료일)을 준다(20261018110000). 2026-10-06 전에는 빈 줄이던 사유 둘도 채운다.
  if (exempt.reason === "promo") {
    return exempt.until
      ? tt("a.83005985", "추천 가입 혜택으로 {{v0}}까지 멤버십 구독료가 없어요. {{v1}}부터 내 등급 멤버십 구독료가 청구됩니다.", { v0: shortDateLabel(exempt.until), v1: shortDateLabel(exempt.resumes_on || addDaysYmd(exempt.until, 1)) })
      : tt("a.9440a783", "추천 가입 혜택으로 지금은 멤버십 구독료가 없어요.");
  }
  if (exempt.reason === "manual") return tt("a.a5e59038", "운영팀이 멤버십 구독료를 면제했어요{{v0}}.", { v0: exempt.until ? tt("a.b4a77927", " ({{v0}}까지)", { v0: shortDateLabel(exempt.until) }) : "" });
  if (exempt.reason === "trial") return tt("a.bc54389a", "무료 기간이에요{{v0}}.", { v0: exempt.until ? tt("a.b4a77927", " ({{v0}}까지)", { v0: shortDateLabel(exempt.until) }) : "" });
  return tt("a.b30fa96d", "지금은 청구되지 않아요.");
}

// ── 결제 완료 화면 (/billing/success) — 시안 docs/design/billing-result-final.html ─────────────
// 구독 정보(C) + 「이제 할 수 있어요」(B) 결합. 이 화면에서 결제 종류마다 갈리는 것은 제목과 「할 수 있어요」 세 줄뿐이고
// 멤버십 상태 카드·이번 결제 상세·결제 내역은 4종 공용이다.
// 🔒 등급 승급권은 환불 불가 상품이라 legal 문장이 다르다 — 결제 전 동의(payment_order.ack `no_refund`)와 같은 말이어야 한다.
//    기본 문장은 약관 제17·20조 및 REFUND_NOTICE_TEXT 와 같은 뜻을 유지할 것.
export const RESULT_LEGAL_DEFAULT = "증빙은 카드 매출전표로 발급되며 세금계산서는 발급하지 않습니다. 결제일 포함 7일 안에 작품 사용 신청 승인·소재 다운로드·검수 신청 없이 해지하면 전액 환불됩니다.";
export const RESULT_LEGAL_NO_REFUND = "등급 승급권은 결제 즉시 개시되며 환불되지 않습니다. 증빙은 카드 매출전표로 발급되며 세금계산서는 발급하지 않습니다.";
// 결제가 없는 결과(카드만 저장 · 가입비 먼저) — 환불할 결제가 없으므로 7일 환불 문장을 붙이지 않는다(2026-10-23).
export const RESULT_LEGAL_NO_CHARGE = "오늘은 결제된 금액이 없습니다. 결제가 생기면 증빙은 카드 매출전표로 발급되며 세금계산서는 발급하지 않습니다.";

// 결제 실패 사유 중 사용자에게 보여도 되는 것만 — 토스가 준 한국어 안내(「카드 한도 초과」)는 그대로, 영어 내부 코드(skipped)·DB 오류 원문(statement timeout)은 「확인 필요」(plain-copy-rules)
// 청구를 건너뛴 이유(_shared/charge.ts skipped 코드) — 사용자가 풀 수 있는 것은 할 일을 말해 준다. 모르는 코드는 「확인 필요」.
const SKIPPED_REASON = {
  signup_fee_required: () => tt("sff.skip.signupFeeFirst", "가입비를 먼저 결제해 주세요 — 멤버십 화면 「지금 할 일」에서 바로 할 수 있어요"),
  signup_gate_failed: () => tt("sff.skip.gateFailed", "확인이 잠시 늦어졌어요 — 내일 10:00에 자동으로 다시 확인돼요"),
  paid_consent_required: () => tt("sff.skip.paidConsent", "멤버십 구독료 변경 동의가 필요해요 — 멤버십 화면에서 동의하면 바로 결제돼요"),
  no_method: () => tt("sff.skip.noMethod", "결제수단이 없어요"),
  consent_check_failed: () => tt("sff.skip.consentCheckFailed", "동의 확인이 잠시 늦어졌어요 — 다음 결제 시각에 다시 확인돼요"),
  proration_check_failed: () => tt("sff.skip.prorationCheckFailed", "이번 달 금액 확인이 잠시 늦어졌어요 — 내일 10:00에 자동으로 다시 확인돼요"),
  not_due: () => tt("sff.skip.notDue", "아직 결제일이 아니에요"),
  reactivation_required: () => tt("sff.skip.reactivation", "재활성화비를 먼저 결제해 주세요"),
  inactive: () => tt("sff.skip.inactive", "지금은 결제 대상이 아니에요"),
  invoice_mode: () => tt("sff.skip.invoice", "청구서로 결제하는 계정이에요"),
  no_subscription: () => tt("sff.skip.noSubscription", "멤버십 정보가 아직 없어요"),
  // chargeUser 가 subscription.status 를 그대로 돌려주는 자리(paused · canceled · cancel_at_period_end)
  paused: () => tt("sff.skip.paused", "멤버십이 중단 상태예요 — 재활성화비를 결제하면 다시 시작돼요"),
  canceled: () => tt("sff.skip.canceled", "해지된 멤버십이에요 — 다시 시작하려면 재활성화비를 결제해 주세요"),
  cancel_at_period_end: () => tt("sff.skip.cancelReserved", "해지가 예약돼 있어 더 청구하지 않아요 — 되돌리면 다시 결제돼요"),
};
/** 청구 결과(ChargeResult)를 사람이 읽는 한 줄로 — 결제 완료 화면·멤버십 화면 토스트가 같은 사전을 쓴다(내부 코드 비노출 · 2026-10-23) */
export function humanChargeReason(charged) {
  const known = SKIPPED_REASON[String(charged?.skipped || "")];
  if (known) return known();
  // 토스 오류는 「CODE: 한국어 안내」 모양(_shared/charge.ts `${e.code}: ${e.message}`) — 앞의 영어 코드는 떼고 한국어 안내만
  const r = String(charged?.reason || "").replace(/^[A-Z0-9_]+:\s*/, "").trim();
  return /[가-힣]/.test(r) ? r : tt("sff.checkNeeded", "확인 필요");
}

const CAN_APPLY = lazyI18n(() => ({ title: tt("a.27a4ade7", "작품 사용 신청"), body: tt("a.f86ec8c9", "원하는 작품을 골라 바로 신청할 수 있어요") }));
const CAN_DOWNLOAD = lazyI18n(() => ({ title: tt("a.89b71d89", "소재 다운로드 · 검수 신청"), body: tt("a.648580de", "승인된 작품의 영상을 내려받고 검수를 신청할 수 있어요") }));

/**
 * 결제 완료 화면 문구.
 * @param {{ kind?: string, order?: object, overview?: object, method?: object, charged?: object, signupFeeRequired?: boolean }} input
 *   kind: signup_fee · reactivation_fee · access_pass · subscription · billing(결제수단 등록)
 *   signupFeeRequired: 결제수단 등록 응답(billing-issue)의 signup_fee_required — true 면 신규 회원이 가입비를 먼저 내야 해서 구독료를 청구하지 않았다 ·
 *     null 이면 서버가 판정을 못 했다(모름) → 「작품 신청 가능」 줄 대신 「멤버십 화면에서 확인」(표시도 fail-closed) (2026-10-23)
 * @returns {{ title: string, subtitle: string, can: {title, body}[], legal: string }}
 */
export function resultCopy({ kind, order, overview, method, charged, signupFeeRequired = false } = {}) {
  const sub = overview?.subscription || {};
  const periodEnd = sub.current_period_end ? String(sub.current_period_end).slice(0, 10) : null;
  const lastDay = periodEnd ? shortDateLabel(addDaysYmd(periodEnd, -1)) : null;
  const tierName = ttl(overview?.tier?.tier_name || tierNameFromBand(overview?.tier?.band) || "");
  const nextLabel = sub.next_charge_at ? nextChargeLabel(sub.next_charge_at) : null;
  const amountLabel = formatKrw(order?.amount);
  const methodLabel = order?.method_label || paymentMethodLabel(method || overview?.payment_method);
  const subtitle = [orderKindLabel(kind), amountLabel, methodLabel].filter(Boolean).join(" · ");

  if (kind === "signup_fee") {
    const bd = order?.breakdown || {};
    // 카드가 아직 없으면(가입비는 1회 결제라 카드가 남지 않는다) 다음 할 일은 카드 등록 — 등록해야 잔여일·월 구독료가 자동 결제되고,
    // 시작 등급 지정 회원은 등록해야 이용이 시작된다(대표 2026-10-20). overview 를 아직 못 읽었으면 종전 문구.
    if (overview && !overview.payment_method && !method) {
      return {
        title: tt("tgp.resultTitleCard", "가입비가 결제됐어요 · 카드를 등록하면 시작돼요"),
        subtitle,
        can: [
          {
            title: tt("tgp.resultCardTitle", "카드 등록하기"),
            body: bd.remainder_from
              ? tt("tgp.resultCardBody", "등록해 두면 {{v0}}부터 그달 남은 날은 날짜만큼, 다음 달 1일부터 월 멤버십 구독료가 자동 결제돼요. 등록하는 날에는 결제되지 않아요.", { v0: shortDateLabel(bd.remainder_from) })
              : tt("tgp.resultCardBodyNext", "등록해 두면 다음 달 1일부터 월 멤버십 구독료가 자동 결제돼요. 등록하는 날에는 결제되지 않아요."),
          },
          {
            title: bd.covers_until ? tt("a.f5cb5596", "{{v0}}까지 실버 등급으로 이용", { v0: shortDateLabel(bd.covers_until) }) : tt("a.a09b910b", "한 달 동안 실버 등급으로 이용"),
            body: tt("tgp.resultCardCoverBody", "이 기간에는 멤버십 구독료가 청구되지 않아요"),
          },
        ],
        next: { label: tt("tgp.resultCardCta", "카드 등록하기"), to: "/billing?register=1" },
        legal: RESULT_LEGAL_DEFAULT,
      };
    }
    return {
      title: tt("a.60b8b5b3", "레이블리를 시작했습니다"),
      subtitle,
      can: [
        CAN_APPLY,
        CAN_DOWNLOAD,
        {
          title: bd.covers_until ? tt("a.f5cb5596", "{{v0}}까지 실버 등급으로 이용", { v0: shortDateLabel(bd.covers_until) }) : tt("a.a09b910b", "한 달 동안 실버 등급으로 이용"),
          body: bd.remainder_from
            ? tt("a.ef09e488", "그 뒤 {{v0}}부터 말일까지는 그달 확정 등급 멤버십 구독료가 일할로 청구돼요", { v0: shortDateLabel(bd.remainder_from) })
            : tt("a.8a35f610", "가입비에 첫 달 이용이 포함돼 있어요"),
        },
      ],
      legal: RESULT_LEGAL_DEFAULT,
    };
  }

  if (kind === "reactivation_fee") {
    // 재활성화비는 멤버십 구독료를 포함하지 않는다 — 그달 멤버십 구독료를 일할해 같은 주문에 함께 받는다(대표 2026-09-23).
    const bundled = Number(order?.breakdown?.subscription_fee || 0);
    return {
      title: tt("a.f13e040e", "멤버십을 다시 시작했습니다"),
      subtitle,
      can: [
        CAN_APPLY,
        CAN_DOWNLOAD,
        {
          title: lastDay ? tt("a.31a364f7", "{{v0}}까지 이번 기간 이용", { v0: lastDay }) : tt("a.04cd9d39", "이번 기간 동안 이용"),
          body: bundled > 0
            ? tt("a.9f2f37fd", "재활성화비와 함께 이번 달 멤버십 구독료 {{v0}}도 결제됐어요{{v1}}", { v0: formatKrw(bundled), v1: nextLabel ? ` · 다음은 ${nextLabel}` : "" })
            : (nextLabel ? tt("a.0ea60301", "{{v0}}에 다음 멤버십 구독료가 결제되면 이어져요", { v0: nextLabel }) : tt("a.1b57b012", "다음 결제일은 멤버십 화면에서 볼 수 있어요")),
        },
      ],
      legal: RESULT_LEGAL_DEFAULT,
    };
  }

  if (kind === "access_pass") {
    const grade = order?.breakdown?.grade === "master" ? "마스터" : "다이아몬드";
    const endsAt = overview?.access_pass?.ends_at ? shortDateLabel(overview.access_pass.ends_at) : null;
    return {
      title: tt("a.c6df7124", "{{v0}} 등급이 됐습니다", { v0: grade }),
      subtitle,
      can: [
        { title: tt("a.647090a4", "{{v0}} 등급 작품 신청", { v0: grade }), body: tt("a.38971286", "그 등급에만 열리는 작품을 신청할 수 있어요") },
        { title: tt("a.d37d456a", "신작 사전 공개 · 드라마 채널 참여"), body: tt("a.8959bf59", "등급 혜택이 지금부터 적용돼요") },
        { title: endsAt ? tt("a.b917c175", "{{v0}}까지 유지", { v0: endsAt }) : tt("a.573002ef", "3개월 동안 유지"), body: tt("a.6c6d427b", "기간이 끝나면 원래 등급으로 돌아가요") },
      ],
      legal: RESULT_LEGAL_NO_REFUND,
    };
  }

  if (kind === "subscription") {
    const monthLabel = order?.month ? tt("a.831d8090", "{{v0}}월", { v0: Number(String(order.month).slice(5, 7)) }) + " " : "";
    return {
      title: tt("a.d71ecc49", "{{v0}}멤버십 구독료가 결제됐습니다", { v0: monthLabel }),
      subtitle,
      can: [
        { title: tt("a.28f22661", "이번 달도 그대로 이용"), body: tt("a.1aef76d4", "작품 사용 신청 · 소재 다운로드 · 검수 신청") },
        { title: tierName ? tt("a.4e3e2601", "{{v0}} 등급 혜택 유지", { v0: tierName }) : tt("a.8b8fae86", "등급 혜택 유지"), body: tt("a.c55ddbd1", "등급은 매월 1일에 다시 정해져요") },
        {
          title: nextLabel ? tt("a.94f0d341", "다음 결제는 {{v0}}", { v0: nextLabel }) : tt("a.dd59bc4a", "다음 결제는 매월 1일 10:00"),
          body: tt("a.87da496e", "결제 7일 전에 금액·결제일을 다시 알려 드려요"),
        },
      ],
      legal: RESULT_LEGAL_DEFAULT,
    };
  }

  // 결제수단 등록(빌링키) — 신규 회원이 가입비를 먼저 내야 하면(서버 billing_signup_fee_gate · 대표 2026-10-23) 구독료는 청구되지 않았고
  // 작품 신청·다운로드도 아직 잠겨 있다. 「작품 신청 가능」이라 말하지 않고 다음 할 일 = 가입비 결제하기.
  if (kind === "billing" && signupFeeRequired) {
    // 금액은 「지금 할 일」과 같은 한 곳(signupCoverage · 서버 billing_fees)에서. overview 를 아직 못 읽었으면 금액 없이 말한다(화면이 금액을 만들지 않는다).
    const feeRaw = overview?.fees?.signup_fee;
    const signupFee = feeRaw != null && feeRaw !== "" && Number.isFinite(Number(feeRaw)) ? formatKrw(signupCoverage(overview.fees, overview.today).amount) : null;
    return {
      title: tt("sff.resultTitle", "카드를 등록했어요 · 가입비를 결제하면 시작돼요"),
      subtitle: paymentMethodLabel(method || overview?.payment_method),
      can: [
        {
          title: signupFee ? tt("sff.resultStepTitle", "가입비 {{v0}} 결제하기", { v0: signupFee }) : tt("a.e53fa159", "가입비 결제하기"),
          body: tt("sff.resultStepBody", "결제일부터 한 달 이용이 포함돼요. 등록한 카드로는 그 기간이 끝난 뒤부터 자동 결제되고, 오늘은 결제되지 않았어요."),
        },
        {
          title: tt("sff.resultLockTitle", "결제 전에는 작품 신청·다운로드가 멈춰 있어요"),
          body: tt("sff.resultLockBody", "가입비를 결제하면 바로 작품을 신청할 수 있어요."),
        },
      ],
      next: { label: tt("a.e53fa159", "가입비 결제하기"), to: "/billing/pay?kind=signup_fee" },
      legal: RESULT_LEGAL_NO_CHARGE,
    };
  }
  // 가입비 문이 답하지 못해 청구를 건너뛴 경우(fail-closed · 내일 10:00 재시도 예약) — 가입비 대상인지 모르므로 「작품 신청 가능」이라 말하지 않고 내부 코드도 보이지 않는다.
  if (kind === "billing" && charged?.skipped === "signup_gate_failed") {
    return {
      title: tt("a.c7ad5d1a", "결제수단을 등록했습니다"),
      subtitle: paymentMethodLabel(method || overview?.payment_method),
      can: [
        { title: tt("sff.gateFailTitle", "오늘은 결제되지 않았어요 · 내일 10:00에 다시 확인해요"), body: tt("sff.gateFailBody", "확인이 잠시 늦어졌어요. 등록한 카드로 내일 자동으로 다시 진행되고, 따로 할 일은 없어요.") },
        { title: tt("sff.gateFailCheckTitle", "작품 신청·다운로드는 멤버십 화면에서 확인"), body: tt("sff.gateFailCheckBody", "「지금 할 일」에 가입비 결제가 뜨면 그것부터 진행해 주세요.") },
      ],
      legal: RESULT_LEGAL_NO_CHARGE,
    };
  }
  // 결제수단 등록(빌링키) — 즉시 청구 결과(charged)를 세 번째 줄에 그대로 말한다.
  const chargedLine = !charged
    ? { title: tt("a.4ebcc486", "다음 결제일에 자동으로 결제돼요"), body: nextLabel ? tt("a.b8f7fd90", "다음은 {{v0}}", { v0: nextLabel }) : tt("a.e1eb7226", "매월 1일 10:00") }
    : charged.ok && charged.skipped === "exempt"
      ? { title: tt("a.73ffd095", "이번 달은 면제라 청구되지 않았어요"), body: tt("a.83473a6e", "면제가 끝나는 달 1일부터 등급 멤버십 구독료가 청구돼요") }
      : charged.ok
        ? { title: tt("a.6b79899c", "이번 달분이 바로 결제됐어요"), body: nextLabel ? tt("a.b8f7fd90", "다음은 {{v0}}", { v0: nextLabel }) : tt("a.dd59bc4a", "다음 결제는 매월 1일 10:00") }
        : { title: tt("a.e5986b34", "이번 달분은 아직 결제되지 않았어요"), body: tt("a.81c9dc27", "멤버십 화면에서 확인해 주세요 ({{v0}})", { v0: humanChargeReason(charged) }) };
  // 가입비 판정을 서버가 못 했으면(null) 「작품 신청·다운로드 가능」·「다음 결제일에 자동 결제」·환불 문구를 단정하지 않는다 — 멤버십 화면 「지금 할 일」이 정답.
  // 첫 버튼도 멤버십 화면으로(작품 보러 가기 아님). 돈이 실제로 나간 결과(charged.ok · 금액 있음)면 그 줄과 환불 문구는 그대로.
  if (signupFeeRequired === null) {
    const charged_ = charged?.ok && !charged?.skipped;
    return {
      title: tt("a.c7ad5d1a", "결제수단을 등록했습니다"),
      subtitle: paymentMethodLabel(method || overview?.payment_method),
      can: [
        { title: tt("sff.gateFailCheckTitle", "작품 신청·다운로드는 멤버십 화면에서 확인"), body: tt("sff.gateFailCheckBody", "「지금 할 일」에 가입비 결제가 뜨면 그것부터 진행해 주세요.") },
        charged ? chargedLine : { title: tt("sff.unknownChargeTitle", "이번 달분 결제는 멤버십 화면에서 확인해요"), body: tt("sff.unknownChargeBody", "가입비 결제가 뜨면 그것부터, 아니면 다음 결제일에 등록한 카드로 결제돼요.") },
      ],
      next: { label: tt("sff.unknownNext", "멤버십 화면에서 확인하기"), to: "/billing" },
      legal: charged_ ? RESULT_LEGAL_DEFAULT : RESULT_LEGAL_NO_CHARGE,
    };
  }
  return {
    title: tt("a.c7ad5d1a", "결제수단을 등록했습니다"),
    subtitle: paymentMethodLabel(method || overview?.payment_method),
    can: [CAN_APPLY, CAN_DOWNLOAD, chargedLine],
    legal: RESULT_LEGAL_DEFAULT,
  };
}
